<template>
  <dialog ref="modal" class="modal">
    <div class="modal-box max-h-[calc(100vh-4rem)]">
      <form method="dialog">
        <button class="btn btn-sm btn-circle btn-ghost absolute top-2 right-2">
          ✕
        </button>
      </form>
      <h3 class="text-lg font-bold">Yêu cầu cập nhật</h3>
      <div class="py-4">
        <div class="mb-4 flex items-center gap-4">
          <select
            v-model="selectedCampaign"
            class="select select-bordered w-full max-w-xs"
            :disabled="isLoadingCampaigns"
          >
            <option
              v-for="campaign in campaigns"
              :key="campaign.id"
              :value="campaign.id"
            >
              {{ campaign.name }}
            </option>
          </select>
        </div>
        <div v-if="isCampaignChanged" class="alert alert-warning mb-4">
          <i class="fa-solid fa-triangle-exclamation"></i>
          <span>
            Máy trạm sẽ bị chuyển sang chiến dịch
            <span class="font-bold">
              {{ selectedCampaignName }}
            </span>
          </span>
        </div>
        <div v-if="isLoadingVersions">Đang tải...</div>
        <div v-else>
          <div v-if="versions.length === 0">
            Không có phiên bản nào khả dụng.
          </div>
          <div v-else>
            <div
              class="max-h-[calc(100vh-15rem)] overflow-x-auto overflow-y-auto"
            >
              <table class="table-pin-rows table">
                <thead>
                  <tr>
                    <th>STT</th>
                    <th>Tên phiên bản</th>
                    <th>Hành động</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(version, index) in versions" :key="index">
                    <td>{{ index + 1 }}</td>
                    <td>{{ version.name }}</td>
                    <td>
                      <button
                        class="btn btn-primary btn-sm"
                        @click="selectVersion(version)"
                      >
                        Chọn
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  </dialog>
</template>

<script setup lang="ts">
import {
  getVersionAgentPagination,
  type VersionAgentItem,
} from "@/api/campaign";
import { useCreateUpdateCommandMutation } from "@/api/updateHistory";
import { use_client_detail } from "@/components/ClientDetail/useClientDetail";
import { useCampaigns } from "@/composables/useCampaigns";
import { useQuery, useQueryClient } from "@tanstack/vue-query";
import { computed, ref, watch } from "vue";

const client_detail_store = use_client_detail();

const modal = ref<HTMLDialogElement | null>(null);
const selectedCampaign = ref<string>("");

const { campaigns, isLoadingCampaigns } = useCampaigns();

// Set default campaign to client's current campaign
watch(
  () => client_detail_store.client_detail?.campaign_id,
  (newCampaign) => {
    if (newCampaign) {
      selectedCampaign.value = newCampaign;
    }
  },
  { immediate: true },
);

const selectedCampaignName = computed(() => {
  return (
    campaigns.value?.find((c) => c.id === selectedCampaign.value)?.name || ""
  );
});

const isCampaignChanged = computed(() => {
  return (
    selectedCampaign.value &&
    selectedCampaign.value !== client_detail_store.client_detail?.campaign_id
  );
});

const { data: versionsData, isFetching: isLoadingVersions } = useQuery({
  queryKey: computed(() => ["availableVersions", selectedCampaign.value]),
  queryFn: () =>
    getVersionAgentPagination({
      page: 1,
      size: 10,
      campaign: selectedCampaign.value,
    }),
  enabled: computed(() => Boolean(selectedCampaign.value)),
});

const versions = computed<VersionAgentItem[]>(
  () => versionsData.value?.data || [],
);

const queryClient = useQueryClient();

const { mutateAsync: createUpdateCommand } = useCreateUpdateCommandMutation();

const selectVersion = async (version: VersionAgentItem) => {
  try {
    await createUpdateCommand({
      agent_id: client_detail_store.id,
      campaign_id: "",
      version_id: version.id,
    });

    // invalidate relevant queries (update history, available versions)
    queryClient.invalidateQueries({ queryKey: ["updateHistory"] });
    queryClient.invalidateQueries({
      queryKey: ["availableVersions", selectedCampaign.value],
    });

    // close modal
    modal.value?.close();
  } catch (err) {
    // createUpdateCommand already shows toasts; log for debugging
    console.error("Failed to create update command", err);
  }
};

defineExpose({
  modal,
});
</script>
